import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Mail } from "lucide-react";
import BackButton from "@/components/Buttons/BackButton";
import { useNavigate } from "react-router-dom";
import VERSION, { DEV_ADDON } from "@/version";
const faqs = [
  {
    question: "How do I start learning with Parrot?",
    answer:
      "Getting started with Parrot is easy! You can jump right in by providing just your name and email—no full account needed to explore the app. If you want to save your progress and track your stats, you can create an account anytime. Once you’re in, dive into our fun, bite-sized lessons from real-world content like TV shows and videos!",
  },
  {
    question: "How does Parrot work?",
    answer:
      "Parrot uses a technique called 'comprehensible input', proven to help learners pick up new languages naturally. We break down real-world content like TV shows and videos into bite-sized lessons with interactive subtitles. As you progress, you’ll complete exercises, earn rewards, and track your stats to stay motivated. Plus, you can plant real trees as you learn!",
  },
  {
    question: "Is Parrot free to use?",
    answer:
      "Yes! Right now, Parrot is completely free to use while we're in our early stages. In the future, we'll introduce a premium version with even more exciting features to help you learn faster. But for now, enjoy all our features on the house!",
  },
  {
    question: "How often should I practice with Parrot?",
    answer:
      "We recommend using Parrot for at least 10-15 minutes a day. Consistency is key when learning a new language, and our lessons are designed to fit into your daily routine. Plus, with our fun gamification system, the more you practice, the more water drops you can earn to plant trees!",
  },
  {
    question: "Can I learn multiple languages with Parrot?",
    answer:
      "Currently, Parrot only offers Spanish, but we're hard at work on adding more languages soon. Keep an eye out for future updates as we expand the options to help you learn even more languages.",
  },
  {
    question: "What makes Parrot different from other language learning apps?",
    answer:
      "Parrot focuses on real-world content and uses comprehensible input to help you learn languages naturally. Our bite-sized lessons, interactive subtitles, and engaging activities keep you immersed while making sure you’re progressing. Plus, our unique gamification system lets you plant real trees as you learn!",
  },
  {
    question: "What are water droplets and how does tree planting work?",
    answer:
      "As you complete lessons and activities in Parrot, you'll earn water droplets. These droplets can be collected and exchanged to plant trees, contributing to your personal “trees planted” stat. For every tree you plant in the app, we also plant a real tree through our partner organization, One Tree Planted. So, the more you learn, the more you help the planet!",
  },
  {
    question: "How do I cancel my subscription?",
    answer:
      "You can cancel your subscription anytime by going to your account settings in the app. If you need help with this, please contact our support team.",
  },
  {
    question: "How do I cancel my subscription on IOS Devices?",
    answer:
      "You can cancel your subscription anytime by going to your IOS device subscriptions settings. If you need help with this, please contact our support team.",
  },
  {
    question: "If I cancel my subscription, will I lose access to Parrot?",
    answer:
      "Your premium will continue until the end of your current billing period. After that, you will have access to Parrot as a free user.",
  },
  {
    question: "How do I get a refund?",
    answer:
      "If you are not satisfied with your purchase, please contact our support team, and we will do our best to help you.",
  },
];

const SupportPage: React.FC = () => {
  const nav = useNavigate();
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-start w-full mb-4">
        <BackButton className="" onBack={() => nav("/")} />
      </div>
      <Card className="w-full max-w-3xl mx-auto">
        <CardHeader>
          <CardTitle className="text-3xl font-bold text-center">
            Parrot Support
          </CardTitle>
          <CardDescription className="text-center">
            Find answers to common questions or reach out to our support team.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem value={`item-${index}`} key={index}>
                <AccordionTrigger className="text-left">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-left">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-8 text-center">
            <h2 className="text-xl font-semibold mb-4">Need more help?</h2>
            <p className="mb-4">
              If you couldn't find the answer you're looking for, please don't
              hesitate to contact our support team.
            </p>
            <Button asChild>
              <a
                href="mailto:support@fluentwithparrot.com"
                className="inline-flex items-center"
              >
                <Mail className="mr-2 h-4 w-4" />
                Contact Support
              </a>
            </Button>
            <div className="text-sm mt-4">support@fluentwithparrot.com</div>
            <div className="text-sm mt-4">
              version: {VERSION} {DEV_ADDON}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SupportPage;
